// ============================================================================
// HIERARCHICAL CONFIG FRAMEWORK — SERVICE
// ============================================================================
// Single facade over the F2 backend surface. Splits into a resolver (consumer)
// path and a full CRUD (admin) path:
//
//   Resolver (used by screens / hooks like useConfig):
//     configService.resolve(configKey, voucherTypeId?)
//     configService.effective(groupKey)
//
//   Groups CRUD:    getGroups / getGroup / createGroup / updateGroup / deleteGroup
//   Definitions:    getDefinitions / … / deleteDefinition
//   Scopes:         getScopes / … / deleteScope
//   Values:         getValues / … / deleteValue
//   List items:     getListItems / … / deleteListItem / reorderListItems
//
// Consumer note: use the useConfig(groupKey) hook (hooks/api/useConfig.ts)
// instead of calling configService.effective() directly from components.
// ============================================================================

import { del, get, post, put } from '../client/axiosClient';
import {
  configDefinitionEndpoints,
  configDiagnosticEndpoints,
  configGroupEndpoints,
  configListItemEndpoints,
  configScopeEndpoints,
  configValueEndpoints,
} from '../endpoints/config.endpoints';
import type { ApiResponse } from '../types';
import type {
  ConfigDefinitionDto,
  ConfigGroupDto,
  ConfigListItemDto,
  ConfigScopeDto,
  ConfigValueDto,
  CreateConfigDefinitionRequest,
  CreateConfigGroupRequest,
  CreateConfigListItemRequest,
  CreateConfigScopeRequest,
  CreateConfigValueRequest,
  EffectiveConfigDto,
  ReorderConfigListItemsRequest,
  ResolvedConfigDto,
  UpdateConfigDefinitionRequest,
  UpdateConfigGroupRequest,
  UpdateConfigListItemRequest,
  UpdateConfigScopeRequest,
  UpdateConfigValueRequest,
} from '../types/config.types';

class ConfigService {
  // ─── diagnostic (consumer-side) ────────────────────────────────────────

  /**
   * Resolve one config key against the current request's scope chain.
   * Backend derives the chain from the standard X-Tenant/Entity/BU/FY
   * headers; the optional voucherTypeId prepends VOUCHER_TYPE scope.
   *
   * Returns null when the key has no definition on the backend.
   */
  async resolve(configKey: string, voucherTypeId?: string): Promise<ResolvedConfigDto | null> {
    const response = await get<ApiResponse<ResolvedConfigDto>>(
      configDiagnosticEndpoints.resolve(configKey, voucherTypeId),
    );
    return response.data.data ?? null;
  }

  /**
   * Batch-resolve every scalar definition in a group in one round trip.
   * Powers useConfig(groupKey) and the Settings screen's scope-chip UI.
   */
  async effective(groupKey: string): Promise<EffectiveConfigDto> {
    const response = await get<ApiResponse<EffectiveConfigDto>>(
      configDiagnosticEndpoints.effective(groupKey),
    );
    return response.data.data ?? { groupKey, entries: [] };
  }

  // ─── groups ────────────────────────────────────────────────────────────

  async getGroups(): Promise<ConfigGroupDto[]> {
    const response = await get<ApiResponse<ConfigGroupDto[]>>(configGroupEndpoints.list());
    return response.data.data ?? [];
  }
  async getGroup(id: string): Promise<ConfigGroupDto | null> {
    const response = await get<ApiResponse<ConfigGroupDto>>(configGroupEndpoints.byId(id));
    return response.data.data ?? null;
  }
  async createGroup(body: CreateConfigGroupRequest): Promise<ConfigGroupDto> {
    const response = await post<ApiResponse<ConfigGroupDto>>(configGroupEndpoints.create(), body);
    return response.data.data!;
  }
  async updateGroup(id: string, body: UpdateConfigGroupRequest): Promise<ConfigGroupDto> {
    const response = await put<ApiResponse<ConfigGroupDto>>(configGroupEndpoints.update(id), body);
    return response.data.data!;
  }
  async deleteGroup(id: string): Promise<void> {
    await del(configGroupEndpoints.delete(id));
  }

  // ─── definitions ───────────────────────────────────────────────────────

  async getDefinitions(groupId?: string, search?: string): Promise<ConfigDefinitionDto[]> {
    const response = await get<ApiResponse<ConfigDefinitionDto[]>>(
      configDefinitionEndpoints.list(groupId, search),
    );
    return response.data.data ?? [];
  }
  async getDefinition(id: string): Promise<ConfigDefinitionDto | null> {
    const response = await get<ApiResponse<ConfigDefinitionDto>>(configDefinitionEndpoints.byId(id));
    return response.data.data ?? null;
  }
  async createDefinition(body: CreateConfigDefinitionRequest): Promise<ConfigDefinitionDto> {
    const response = await post<ApiResponse<ConfigDefinitionDto>>(configDefinitionEndpoints.create(), body);
    return response.data.data!;
  }
  async updateDefinition(id: string, body: UpdateConfigDefinitionRequest): Promise<ConfigDefinitionDto> {
    const response = await put<ApiResponse<ConfigDefinitionDto>>(configDefinitionEndpoints.update(id), body);
    return response.data.data!;
  }
  async deleteDefinition(id: string): Promise<void> {
    await del(configDefinitionEndpoints.delete(id));
  }

  // ─── scopes ────────────────────────────────────────────────────────────

  async getScopes(scopeType?: string, parentScopeId?: string): Promise<ConfigScopeDto[]> {
    const response = await get<ApiResponse<ConfigScopeDto[]>>(
      configScopeEndpoints.list(scopeType, parentScopeId),
    );
    return response.data.data ?? [];
  }
  async getScope(id: string): Promise<ConfigScopeDto | null> {
    const response = await get<ApiResponse<ConfigScopeDto>>(configScopeEndpoints.byId(id));
    return response.data.data ?? null;
  }
  async createScope(body: CreateConfigScopeRequest): Promise<ConfigScopeDto> {
    const response = await post<ApiResponse<ConfigScopeDto>>(configScopeEndpoints.create(), body);
    return response.data.data!;
  }
  async updateScope(id: string, body: UpdateConfigScopeRequest): Promise<ConfigScopeDto> {
    const response = await put<ApiResponse<ConfigScopeDto>>(configScopeEndpoints.update(id), body);
    return response.data.data!;
  }
  async deleteScope(id: string): Promise<void> {
    await del(configScopeEndpoints.delete(id));
  }

  // ─── values ────────────────────────────────────────────────────────────

  async getValues(definitionId?: string, scopeId?: string): Promise<ConfigValueDto[]> {
    const response = await get<ApiResponse<ConfigValueDto[]>>(
      configValueEndpoints.list(definitionId, scopeId),
    );
    return response.data.data ?? [];
  }
  async getValue(id: string): Promise<ConfigValueDto | null> {
    const response = await get<ApiResponse<ConfigValueDto>>(configValueEndpoints.byId(id));
    return response.data.data ?? null;
  }
  async createValue(body: CreateConfigValueRequest): Promise<ConfigValueDto> {
    const response = await post<ApiResponse<ConfigValueDto>>(configValueEndpoints.create(), body);
    return response.data.data!;
  }
  async updateValue(id: string, body: UpdateConfigValueRequest): Promise<ConfigValueDto> {
    const response = await put<ApiResponse<ConfigValueDto>>(configValueEndpoints.update(id), body);
    return response.data.data!;
  }
  async deleteValue(id: string): Promise<void> {
    await del(configValueEndpoints.delete(id));
  }

  // ─── list items ────────────────────────────────────────────────────────

  async getListItems(definitionId: string, scopeId: string): Promise<ConfigListItemDto[]> {
    const response = await get<ApiResponse<ConfigListItemDto[]>>(
      configListItemEndpoints.list(definitionId, scopeId),
    );
    return response.data.data ?? [];
  }
  async getListItem(id: string): Promise<ConfigListItemDto | null> {
    const response = await get<ApiResponse<ConfigListItemDto>>(configListItemEndpoints.byId(id));
    return response.data.data ?? null;
  }
  async createListItem(body: CreateConfigListItemRequest): Promise<ConfigListItemDto> {
    const response = await post<ApiResponse<ConfigListItemDto>>(configListItemEndpoints.create(), body);
    return response.data.data!;
  }
  async updateListItem(id: string, body: UpdateConfigListItemRequest): Promise<ConfigListItemDto> {
    const response = await put<ApiResponse<ConfigListItemDto>>(configListItemEndpoints.update(id), body);
    return response.data.data!;
  }
  async deleteListItem(id: string): Promise<void> {
    await del(configListItemEndpoints.delete(id));
  }
  /** Bulk-reorder items within one (definition, scope). Backend two-phase bumps to avoid unique-index conflicts. */
  async reorderListItems(body: ReorderConfigListItemsRequest): Promise<number> {
    const response = await post<ApiResponse<{ affected: number }>>(configListItemEndpoints.reorder(), body);
    return response.data.data?.affected ?? 0;
  }
}

export const configService = new ConfigService();
export default configService;
